import React, { useState } from 'react';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import Modal from 'components/Modal';
import { postGitBranches, deleteGitBranches } from '../../utils/api.service';
import { addBranch, removeBranch } from '../../store/actions/hosting_actions';
import { addNewNotification } from 'store/actions/notification_actions';
import classNames from 'classnames';
import LoadingSpinner from 'components/LoadingSpinner';

const BranchModal = ({
  isOpen,
  onClose = () => {},
  onSave = () => {},
  teams = [],
  awsaccounts = [],
  workstream = '',
  org = '',
}) => {
  // Group branches by teamname, awsaccountid, gitrepo
  function groupBranches(branches) {
    const groups = {};
    branches.forEach((branch) => {
      const key = `${branch.teamname}||${branch.awsaccountid}||${branch.gitrepo}`;
      if (!groups[key]) {
        groups[key] = {
          teamname: branch.teamname,
          awsaccountid: branch.awsaccountid,
          gitrepo: branch.gitrepo,
          branches: [],
        };
      }
      groups[key].branches.push(branch.branchname);
    });
    return Object.values(groups);
  }

  // Initial values: group by team/aws/gitrepo, each with array of branchnames
  const branchesFromState = useSelector(
    (state) => state.hosting.branches || []
  );
  const formikRef = React.useRef();
  const dispatch = useDispatch();

  // Track pending branches per group index: { [groupIdx]: { branchName: { status: 'pending'|'success'|'error' } } }
  const [pendingBranches, setPendingBranches] = useState({}); // { [groupIdx]: { [branchName]: { status } } }
  const [addingBranch, setAddingBranch] = useState(false);
  // Add state to track which branch is being deleted per group
  const [deletingBranch, setDeletingBranch] = useState({}); // { [groupIdx]: branchName | null }

  // For new UI, initial values are groups
  const initialValues = {
    groups: branchesFromState.length
      ? groupBranches(branchesFromState).map((g) => ({
          teamname: g.teamname || '',
          awsaccountid: g.awsaccountid || '',
          gitrepo: g.gitrepo || '',
          branches: g.branches || [],
          newBranch: '',
        }))
      : [
          {
            teamname: '',
            awsaccountid: '',
            gitrepo: '',
            branches: [],
            newBranch: '',
          },
        ],
  };

  // Validation
  const validationSchema = Yup.object().shape({
    groups: Yup.array()
      .of(
        Yup.object().shape({
          teamname: Yup.string().required('Team is required'),
          awsaccountid: Yup.string().required('AWS Account is required'),
          gitrepo: Yup.string().required('Git Repo is required'),
          branches: Yup.array()
            .of(Yup.string().required('Branch is required'))
            .min(1, 'At least one branch is required'),
        })
      )
      .min(1, 'At least one group is required'),
  });

  // Helper to update pendingBranches state
  const updatePendingBranch = (groupIdx, branchName, status) => {
    setPendingBranches((prev) => ({
      ...prev,
      [groupIdx]: {
        ...(prev[groupIdx] || {}),
        [branchName]: { status },
      },
    }));
  };

  // Helper to remove pending branch from state
  const removePendingBranch = (groupIdx, branchName) => {
    setPendingBranches((prev) => {
      const group = { ...(prev[groupIdx] || {}) };
      delete group[branchName];
      const next = { ...prev, [groupIdx]: group };
      // Clean up empty group
      if (Object.keys(group).length === 0) delete next[groupIdx];
      return next;
    });
  };

  const handleAddBranch = async ({
    group,
    groupIdx,
    groupPending,
    setFieldValue,
    value,
  }) => {
    const val = value.trim();
    // Validation: require team, awsaccount, gitrepo before adding branch
    if (!group.teamname || !group.awsaccountid || !group.gitrepo) {
      await formikRef.current.validateForm();
      formikRef.current.setFieldTouched(
        `groups.${groupIdx}.teamname`,
        true,
        false
      );
      formikRef.current.setFieldTouched(
        `groups.${groupIdx}.awsaccountid`,
        true,
        false
      );
      formikRef.current.setFieldTouched(
        `groups.${groupIdx}.gitrepo`,
        true,
        false
      );
      return;
    }
    if (val && !group.branches.includes(val) && !groupPending[val]) {
      setAddingBranch(true);
      // Optimistically add as pending
      setFieldValue(`groups.${groupIdx}.branches`, [...group.branches, val]);
      updatePendingBranch(groupIdx, val, 'pending');
      setFieldValue(`groups.${groupIdx}.newBranch`, '');
      // Call API
      const branchObj = {
        teamname: group.teamname,
        awsaccountid: group.awsaccountid,
        gitrepo: group.gitrepo,
        branchname: val,
      };
      postGitBranches(org, workstream, {
        branches: [branchObj],
      })
        .then(() => {
          updatePendingBranch(groupIdx, val, 'success');
          dispatch(addBranch(branchObj));
          dispatch(
            addNewNotification(`Branch ${val} added successfully.`, 'success')
          );
        })
        .catch((error) => {
          // Remove from UI
          setFieldValue(
            `groups.${groupIdx}.branches`,
            group.branches.filter((b) => b !== val)
          );
          removePendingBranch(groupIdx, val);
          dispatch(
            addNewNotification(
              `Failed to add branch ${val}: ${
                error.response?.data?.detail ||
                'An error occurred while adding the branch.'
              }`,
              'danger'
            )
          );
        })
        .finally(() => {
          setAddingBranch(false);
        });
    }
  };

  // Handler for deleting a branch
  const handleDeleteBranch = async ({
    group,
    groupIdx,
    branch,
    branchIdx,
    setFieldValue,
  }) => {
    setDeletingBranch((prev) => ({
      ...prev,
      [groupIdx]: branch,
    }));
    try {
      const branchObj = {
        teamname: group.teamname,
        awsaccountid: group.awsaccountid,
        gitrepo: group.gitrepo,
        branchname: branch,
      };
      await deleteGitBranches(org, workstream, { branches: [branchObj] });
      dispatch(removeBranch(branchIdx));
      dispatch(
        addNewNotification(`Branch ${branch} removed successfully.`, 'success')
      );
      // Remove from UI
      const updated = [...group.branches];
      updated.splice(branchIdx, 1);
      setFieldValue(`groups.${groupIdx}.branches`, updated);
    } catch (error) {
      dispatch(
        addNewNotification(
          `Failed to remove branch ${branch}: ${
            error.response?.data?.detail ||
            'An error occurred while removing the branch.'
          }`,
          'danger'
        )
      );
      // No UI change, just restore x button
    } finally {
      setDeletingBranch((prev) => ({
        ...prev,
        [groupIdx]: null,
      }));
    }
  };

  return (
    <Modal
      title="Manage Git Branch"
      submitButtonText={null}
      cancelButtonText="Close"
      submitEnabled={false}
      onClose={onClose}
      onSubmit={null}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={() => {}}
        validateOnChange={true}
        validateOnBlur={true}
        innerRef={formikRef}
      >
        {({ values, errors, setFieldValue }) => (
          <Form className="needs-validation" noValidate>
            <FieldArray name="groups">
              {({ insert, remove, push }) => (
                <div>
                  <div className="branch-modal-header-row">
                    <div className="branch-modal-header-col">Team Name</div>
                    <div className="branch-modal-header-col">AWS Account</div>
                    <div className="branch-modal-header-col">Git Repo URL</div>
                    <div className="branch-modal-header-col">Git Branches</div>
                    <div className="branch-modal-header-col branch-modal-header-action"></div>
                  </div>
                  {values.groups.map((group, groupIdx) => {
                    const awsOptions =
                      group.teamname && awsaccounts[group.teamname]
                        ? awsaccounts[group.teamname]
                        : [];
                    const groupPending = pendingBranches[groupIdx] || {};
                    const isAnyPending = Object.values(pendingBranches).some(
                      (groupObj) =>
                        Object.values(groupObj || {}).some(
                          (b) => b.status === 'pending'
                        )
                    );
                    return (
                      <React.Fragment key={groupIdx}>
                        <div className="branch-modal-group-row">
                          {/* Team Name */}
                          <div className="branch-modal-col">
                            <Field
                              as="select"
                              name={`groups.${groupIdx}.teamname`}
                              className={`form-control ${
                                errors.groups?.[groupIdx]?.teamname
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              onChange={(e) => {
                                setFieldValue(
                                  `groups.${groupIdx}.teamname`,
                                  e.target.value
                                );
                                if (e.target.value) {
                                  formikRef.current.setFieldTouched(
                                    `groups.${groupIdx}.teamname`,
                                    false,
                                    false
                                  );
                                }
                              }}
                            >
                              <option value="" label="Select team" />
                              {Object.keys(awsaccounts).map((team) => (
                                <option key={team} value={team} label={team} />
                              ))}
                            </Field>
                            <ErrorMessage
                              name={`groups.${groupIdx}.teamname`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                          {/* AWS Account */}
                          <div className="branch-modal-col">
                            <Field
                              as="select"
                              name={`groups.${groupIdx}.awsaccountid`}
                              className={`form-control ${
                                errors.groups?.[groupIdx]?.awsaccountid
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              onChange={(e) => {
                                setFieldValue(
                                  `groups.${groupIdx}.awsaccountid`,
                                  e.target.value
                                );
                                if (e.target.value) {
                                  formikRef.current.setFieldTouched(
                                    `groups.${groupIdx}.awsaccountid`,
                                    false,
                                    false
                                  );
                                }
                              }}
                            >
                              <option value="" label="Select AWS Account" />
                              {awsOptions.map((acc) => (
                                <option
                                  key={acc.id}
                                  value={acc.id}
                                  label={acc.name}
                                />
                              ))}
                            </Field>
                            <ErrorMessage
                              name={`groups.${groupIdx}.awsaccountid`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                          {/* Git Repo */}
                          <div className="branch-modal-col branch-modal-col-repo">
                            <Field
                              type="text"
                              name={`groups.${groupIdx}.gitrepo`}
                              placeholder="Git Repo URL"
                              className={`form-control ${
                                errors.groups?.[groupIdx]?.gitrepo
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              onChange={(e) => {
                                setFieldValue(
                                  `groups.${groupIdx}.gitrepo`,
                                  e.target.value
                                );
                                if (e.target.value) {
                                  formikRef.current.setFieldTouched(
                                    `groups.${groupIdx}.gitrepo`,
                                    false,
                                    false
                                  );
                                }
                              }}
                            />
                            <ErrorMessage
                              name={`groups.${groupIdx}.gitrepo`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                          {/* Branch name input and add button */}
                          <div className="branch-modal-col branch-modal-col-branchinput">
                            <div className="branch-modal-branchinput-row">
                              <Field
                                type="text"
                                name={`groups.${groupIdx}.newBranch`}
                                placeholder="Add branch and press Enter"
                                className="form-control branch-modal-branchinput"
                                disabled={isAnyPending}
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    await handleAddBranch({
                                      group,
                                      groupIdx,
                                      groupPending,
                                      setFieldValue,
                                      value: e.target.value,
                                    });
                                  }
                                }}
                                value={group.newBranch}
                                onChange={(e) =>
                                  setFieldValue(
                                    `groups.${groupIdx}.newBranch`,
                                    e.target.value
                                  )
                                }
                              />
                              <button
                                type="button"
                                className="btn btn-success branch-modal-add-btn"
                                disabled={
                                  isAnyPending ||
                                  !group.newBranch.trim() ||
                                  group.branches.includes(
                                    group.newBranch.trim()
                                  ) ||
                                  !!groupPending[group.newBranch.trim()]
                                }
                                onClick={async () => {
                                  await handleAddBranch({
                                    group,
                                    groupIdx,
                                    groupPending,
                                    setFieldValue,
                                    value: group.newBranch,
                                  });
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                        {group.branches.length > 0 && (
                          <div className="branch-modal-branches-list">
                            {group.branches.map((branch, branchIdx) => {
                              const pending =
                                groupPending[branch]?.status === 'pending';
                              const deleting =
                                deletingBranch[groupIdx] === branch;
                              const anyDeleting =
                                Object.values(deletingBranch).some(Boolean);
                              return (
                                <span
                                  key={branchIdx}
                                  className={classNames(
                                    'branch-modal-branch-pill',
                                    {
                                      'branch-modal-branch-pill-pending':
                                        pending || deleting,
                                    }
                                  )}
                                >
                                  {pending ? (
                                    <LoadingSpinner
                                      text={`Adding ${branch}...`}
                                      className="branch-modal-loading-spinner"
                                    />
                                  ) : deleting ? (
                                    <LoadingSpinner
                                      text={`Removing ${branch}...`}
                                      className="branch-modal-loading-spinner"
                                    />
                                  ) : (
                                    branch
                                  )}
                                  {!pending && !deleting && (
                                    <button
                                      type="button"
                                      className="branch-modal-remove-branch"
                                      disabled={anyDeleting}
                                      style={{
                                        display: deleting ? 'none' : undefined,
                                      }}
                                      onClick={async () => {
                                        await handleDeleteBranch({
                                          group,
                                          groupIdx,
                                          branch,
                                          branchIdx,
                                          setFieldValue,
                                        });
                                      }}
                                      aria-label="Remove branch"
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ marginTop: '10px' }}
                    onClick={() => {
                      push({
                        teamname: '',
                        awsaccountid: '',
                        gitrepo: '',
                        branches: [],
                        newBranch: '',
                      });
                    }}
                    disabled={teams.length <= 1}
                  >
                    Add Team
                  </button>
                </div>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default BranchModal;
